import * as React from "react";
import {
  MagnifyingGlass,
  CaretDown,
  X,
  Calendar as CalendarIcon,
  Funnel,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type {
  InvoiceListFilters,
  InvoiceLabels,
  InvoiceStatus,
  InvoiceType,
  InvoiceDateRange,
} from "./types";
import { hasActiveFilters } from "./utils";

interface Props {
  filters: InvoiceListFilters;
  labels: InvoiceLabels;
  onChange: (next: InvoiceListFilters) => void;
}

const STATUS_OPTIONS: InvoiceStatus[] = [
  "draft",
  "issued",
  "sent",
  "paid",
  "voided",
  "failed",
];

const TYPE_OPTIONS: InvoiceType[] = ["e_arsiv", "e_fatura"];

export function InvoiceFilterBar({ filters, labels, onChange }: Props) {
  const [searchInput, setSearchInput] = React.useState(filters.search);
  const debounceRef = React.useRef<number | null>(null);

  React.useEffect(() => setSearchInput(filters.search), [filters.search]);

  React.useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      if (searchInput !== filters.search) {
        onChange({ ...filters, search: searchInput, page: 1 });
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const toggleStatus = (s: InvoiceStatus) => {
    const has = filters.status.includes(s);
    onChange({
      ...filters,
      status: has ? filters.status.filter((x) => x !== s) : [...filters.status, s],
      page: 1,
    });
  };

  const toggleType = (t: InvoiceType) => {
    const has = filters.type.includes(t);
    onChange({
      ...filters,
      type: has ? filters.type.filter((x) => x !== t) : [...filters.type, t],
      page: 1,
    });
  };

  const clear = () =>
    onChange({
      ...filters,
      search: "",
      status: [],
      type: [],
      dateRange: null,
      customerId: null,
      page: 1,
    });

  const setRange = (range: InvoiceDateRange | null) =>
    onChange({ ...filters, dateRange: range, page: 1 });

  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const presetToday = (): InvoiceDateRange => ({ from: iso(today), to: iso(today) });
  const presetLast = (n: number): InvoiceDateRange => {
    const from = new Date(today);
    from.setDate(today.getDate() - n);
    return { from: iso(from), to: iso(today) };
  };
  const presetThisMonth = (): InvoiceDateRange => {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(from), to: iso(today) };
  };

  const dateLabel = filters.dateRange
    ? `${filters.dateRange.from} → ${filters.dateRange.to}`
    : labels.filters.dateRange;

  return (
    <div className="flex flex-wrap items-center gap-2 bg-biz-surface-elevated border border-biz-border rounded-[10px] p-2.5 biz-shadow-sm">
      {/* Search */}
      <div className="relative flex-1 min-w-[220px]">
        <MagnifyingGlass
          size={16}
          weight="regular"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-subtle pointer-events-none"
        />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={labels.filters.search}
          aria-label={labels.filters.search}
          className="w-full h-10 pl-9 pr-3 bg-biz-bg border border-biz-border rounded-biz-btn text-sm text-biz-foreground placeholder:text-biz-foreground-subtle biz-focus-ring"
        />
      </div>

      {/* Status filter */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border text-sm font-medium biz-focus-ring transition-colors",
              filters.status.length > 0
                ? "bg-biz-primary-subtle text-biz-primary-subtle-fg border-biz-primary/30"
                : "bg-biz-bg border-biz-border text-biz-foreground hover:bg-biz-muted",
            )}
          >
            <Funnel size={14} weight="regular" />
            {labels.filters.status}
            {filters.status.length > 0 && (
              <span className="font-mono-num text-[11px] bg-biz-primary text-biz-primary-foreground rounded-full px-1.5">
                {filters.status.length}
              </span>
            )}
            <CaretDown size={12} weight="bold" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2 bg-biz-surface-elevated border-biz-border">
          <div className="flex flex-col gap-0.5">
            {STATUS_OPTIONS.map((s) => {
              const checked = filters.status.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  aria-pressed={checked}
                  className={cn(
                    "h-9 px-2 flex items-center gap-2 rounded-biz-btn text-sm text-left biz-focus-ring",
                    checked ? "bg-biz-primary-subtle text-biz-primary-subtle-fg" : "hover:bg-biz-muted text-biz-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center",
                      checked ? "bg-biz-primary border-biz-primary text-biz-primary-foreground" : "border-biz-border-strong",
                    )}
                  >
                    {checked && <span className="text-[10px]">✓</span>}
                  </span>
                  {labels.status[s]}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Type filter */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border text-sm font-medium biz-focus-ring transition-colors",
              filters.type.length > 0
                ? "bg-biz-primary-subtle text-biz-primary-subtle-fg border-biz-primary/30"
                : "bg-biz-bg border-biz-border text-biz-foreground hover:bg-biz-muted",
            )}
          >
            {labels.filters.type}
            {filters.type.length > 0 && (
              <span className="font-mono-num text-[11px] bg-biz-primary text-biz-primary-foreground rounded-full px-1.5">
                {filters.type.length}
              </span>
            )}
            <CaretDown size={12} weight="bold" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-44 p-2 bg-biz-surface-elevated border-biz-border">
          <div className="flex flex-col gap-0.5">
            {TYPE_OPTIONS.map((t) => {
              const checked = filters.type.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  aria-pressed={checked}
                  className={cn(
                    "h-9 px-2 flex items-center gap-2 rounded-biz-btn text-sm text-left biz-focus-ring",
                    checked ? "bg-biz-primary-subtle text-biz-primary-subtle-fg" : "hover:bg-biz-muted text-biz-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center",
                      checked ? "bg-biz-primary border-biz-primary text-biz-primary-foreground" : "border-biz-border-strong",
                    )}
                  >
                    {checked && <span className="text-[10px]">✓</span>}
                  </span>
                  {labels.type[t]}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Date range */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border text-sm font-medium biz-focus-ring transition-colors",
              filters.dateRange
                ? "bg-biz-primary-subtle text-biz-primary-subtle-fg border-biz-primary/30"
                : "bg-biz-bg border-biz-border text-biz-foreground hover:bg-biz-muted",
            )}
          >
            <CalendarIcon size={14} weight="regular" />
            <span className="font-mono-num text-[12px]">{dateLabel}</span>
            <CaretDown size={12} weight="bold" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-3 bg-biz-surface-elevated border-biz-border">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              { key: "today", label: labels.filters.today, range: presetToday() },
              { key: "last7", label: labels.filters.last7, range: presetLast(7) },
              { key: "last30", label: labels.filters.last30, range: presetLast(30) },
              { key: "month", label: labels.filters.thisMonth, range: presetThisMonth() },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setRange(p.range)}
                className="h-8 px-2.5 text-xs rounded-full border border-biz-border bg-biz-bg hover:bg-biz-muted text-biz-foreground biz-focus-ring"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs text-biz-foreground-muted">
              {labels.filters.from}
              <input
                type="date"
                value={filters.dateRange?.from ?? ""}
                onChange={(e) =>
                  setRange({
                    from: e.target.value,
                    to: filters.dateRange?.to ?? e.target.value,
                  })
                }
                className="h-9 px-2 bg-biz-bg border border-biz-border rounded-biz-btn text-sm biz-focus-ring"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-biz-foreground-muted">
              {labels.filters.to}
              <input
                type="date"
                value={filters.dateRange?.to ?? ""}
                onChange={(e) =>
                  setRange({
                    from: filters.dateRange?.from ?? e.target.value,
                    to: e.target.value,
                  })
                }
                className="h-9 px-2 bg-biz-bg border border-biz-border rounded-biz-btn text-sm biz-focus-ring"
              />
            </label>
          </div>
          {filters.dateRange && (
            <button
              type="button"
              onClick={() => setRange(null)}
              className="mt-3 text-xs text-biz-foreground-muted hover:text-biz-foreground biz-focus-ring"
            >
              {labels.filters.clear}
            </button>
          )}
        </PopoverContent>
      </Popover>

      {/* Clear */}
      {hasActiveFilters(filters) && (
        <button
          type="button"
          onClick={clear}
          className="h-10 px-3 inline-flex items-center gap-1.5 text-sm font-medium text-biz-foreground-muted hover:text-biz-foreground biz-focus-ring"
        >
          <X size={14} weight="bold" />
          {labels.filters.clear}
        </button>
      )}
    </div>
  );
}
